import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: "SUPERADMIN" | "DD_ACCOUNT_MANAGER" | "ORG_ADMIN" | "TERRITORY_SALES_MANAGER" | "SALES";
  is_superuser?: boolean;
  /** DD staff capability flags (superadmins always have these). */
  can_pull_paynet?: boolean;
  can_submit_any_lender?: boolean;
  organization?: number | null;
  organization_name?: string | null;
  organization_slug?: string | null;
  organization_type?: string | null;
  /** False when org is PENDING; dealer cannot create applications until ACTIVE */
  organization_is_active?: boolean;
  /** Present only for the logged-in account manager. */
  signup_link?: string | null;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const { data } = await customInstance.get<User>("/api/auth/me/");
      return data;
    },
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 502 || status === 503) return failureCount < 2;
      return false;
    },
    enabled: true,
    staleTime: Infinity, // Never refetch automatically - only on explicit refetch
    gcTime: Infinity, // Keep in cache indefinitely
  });

  useEffect(() => {
    // Always sync user state with query data immediately
    if (data) {
      setUser(data);
    } else if (error && error.response?.status === 401 && !isLoading) {
      // Only clear user on 401 (unauthorized) errors, not on other errors
      // This means the session is invalid
      setUser(null);
    }
    // If data is null/undefined but no error, keep current user state
    // This prevents clearing user during initial load or refetch
  }, [data, error, isLoading]);

  // Use data directly if user state hasn't been set yet (prevents race conditions)
  const currentUser = user || data || null;

  const login = async (email: string, password: string) => {
    const { data } = await customInstance.post<User>("/api/auth/login/", { email, password });
    setUser(data);
    await refetch();
  };

  const logout = async () => {
    await customInstance.post("/api/auth/logout/");
    setUser(null);
    queryClient.removeQueries({ queryKey: ["auth", "me"] });
  };

  // isAuthenticated should check both user state and query data
  // This prevents race conditions where data exists but user state hasn't updated yet
  const isAuthenticated = !!currentUser;

  return (
    <AuthContext.Provider
      value={{
        user: currentUser,
        isLoading,
        isAuthenticated,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}







